import { Sparkles, Loader2 } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function GeneratorPanel({
    targetLanguage,
    setTargetLanguage,
    level,
    setLevel,
    theme,
    setTheme,
    onGenerate,
    isGenerating,
    targetLanguages,
    levels,
    themes,
}) {
    return (
        <div
            className="bg-white border border-lexiBorder rounded-2xl p-6 md:p-8 space-y-6"
            data-testid="generator-panel"
        >
            <div>
                <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-2">
                    Générateur
                </p>
                <h2 className="font-serif text-2xl leading-tight">Composez votre leçon</h2>
            </div>

            {/* Target language */}
            <div className="space-y-2">
                <Label
                    htmlFor="target-language"
                    className="text-xs tracking-[0.18em] uppercase text-inkSoft font-semibold"
                >
                    Langue cible
                </Label>
                <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                    <SelectTrigger
                        id="target-language"
                        data-testid="select-target-language"
                        className="rounded-xl border-lexiBorder bg-paper hover:bg-paperAlt transition-colors h-11"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                        {targetLanguages.map((l) => (
                            <SelectItem
                                key={l.label}
                                value={l.label}
                                data-testid={`option-language-${l.label}`}
                            >
                                <span className="mr-2">{l.flag}</span>
                                <span className="capitalize">{l.label}</span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Level */}
            <div className="space-y-2">
                <Label
                    className="text-xs tracking-[0.18em] uppercase text-inkSoft font-semibold"
                >
                    Niveau CEFR
                </Label>
                <div className="flex flex-wrap gap-2" data-testid="level-picker">
                    {levels.map((l) => {
                        const active = l === level;
                        return (
                            <button
                                key={l}
                                onClick={() => setLevel(l)}
                                data-testid={`level-${l}`}
                                className={`px-3 py-1.5 rounded-full text-sm font-mono border transition-colors ${
                                    active
                                        ? "bg-ink text-white border-ink"
                                        : "bg-white text-ink border-lexiBorder hover:bg-paperAlt"
                                }`}
                            >
                                {l}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Theme */}
            <div className="space-y-2">
                <Label
                    htmlFor="theme-input"
                    className="text-xs tracking-[0.18em] uppercase text-inkSoft font-semibold"
                >
                    Thème
                </Label>
                <Input
                    id="theme-input"
                    data-testid="theme-input"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    placeholder="ex. nourriture, voyage…"
                    className="rounded-xl border-lexiBorder bg-paper hover:bg-paperAlt h-11 focus-visible:ring-2 focus-visible:ring-terracotta"
                />
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="theme-suggestions">
                    {themes.slice(0, 6).map((t) => (
                        <button
                            key={t}
                            onClick={() => setTheme(t)}
                            data-testid={`theme-chip-${t}`}
                            className="text-[11px] px-2.5 py-1 rounded-full border border-lexiBorder text-inkSoft hover:text-ink hover:bg-paperAlt transition-colors"
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            <Button
                onClick={onGenerate}
                disabled={isGenerating}
                data-testid="generate-lesson-btn"
                className="w-full h-11 rounded-full bg-terracotta hover:bg-terracottaDark text-white text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
            >
                {isGenerating ? (
                    <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Génération…
                    </>
                ) : (
                    <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Générer
                    </>
                )}
            </Button>

            <p className="text-[11px] text-inkSoft leading-relaxed pt-2 border-t border-lexiBorder">
                Modèle : GPT-4.1 · TTS : OpenAI tts-1 · Transcription : Whisper.
                Clé Emergent universelle utilisée côté serveur uniquement.
            </p>
        </div>
    );
}
